import { useCallback, useEffect, useRef, useState } from "react";

import useYouTubeConsent from "../YouTubeConsent/useYouTubeConsent";

// A controllable YouTube player, rather than a bare <iframe src>.
//
// The only reason to take on the IFrame API is control: capturing a word means typing
// next to a playing video, and without pauseVideo()/playVideo() every capture costs the
// learner five seconds of missed audio. Both methods are documented and permitted —
// unlike the undocumented captions module, which is not, and which is why captions are
// still requested through playerVars rather than set directly.

const API_SRC = "https://www.youtube.com/iframe_api";

// Long enough that a slow connection isn't mistaken for a block, short enough that a
// blocked script doesn't leave someone staring at a black rectangle.
const API_TIMEOUT_MS = 8000;

// One script for the whole page, and one promise however many components ask for it.
let apiPromise = null;

function loadApi() {
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }

    // The API calls a single global when it finishes loading, so chain rather than
    // clobber — something else on the page may have got there first.
    const previous = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (previous) previous();
      resolve(window.YT);
    };

    const script = document.createElement("script");
    script.src = API_SRC;

    // A blocked script may fire onerror, or may be silently dropped with the element left
    // in the DOM and nothing else happening - which blocker you have decides. So: react to
    // onerror if it comes, and give up on a timer if it doesn't, because a promise that
    // stays pending forever is a player that never appears and never says why.
    script.onerror = () => reject(new Error(`Could not load ${API_SRC}`));

    setTimeout(() => reject(new Error(`Timed out loading ${API_SRC}`)), API_TIMEOUT_MS);

    document.head.appendChild(script);
  });

  return apiPromise;
}

export default function useYouTubePlayer({ videoId, captionLanguage }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const loadedRef = useRef(null);

  // Read inside the construction effect without making it a dependency — rebuilding the
  // player on every clip change would flash, where loadVideoById swaps in place.
  const videoIdRef = useRef(videoId);
  videoIdRef.current = videoId;

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Blockers and privacy extensions commonly refuse youtube.com/iframe_api - it's on
  // several blocklists. The script element still lands in the DOM, so the only way to know
  // is that it never executes. Callers fall back to a plain embed, which isn't blocked
  // because it's an ordinary iframe rather than a script.
  const [failed, setFailed] = useState(false);

  const { acceptCookies, consented } = useYouTubeConsent();

  // playerVars are fixed at construction, so a change of caption language genuinely does
  // need a new player. In practice every clip in one seed file shares a language, so this
  // runs once.
  useEffect(() => {
    if (!consented) return undefined;

    let cancelled = false;
    setReady(false);

    loadApi().then((YT) => {
      if (cancelled || !containerRef.current) return;

      loadedRef.current = videoIdRef.current;

      playerRef.current = new YT.Player(containerRef.current, {
        events: {
          onReady: () => {
            if (!cancelled) setReady(true);
          },
          // Tracked so our own play/pause button can show the right label even when the
          // learner drove playback from YouTube's controls instead.
          onStateChange: (event) => {
            if (!cancelled) setPlaying(event.data === 1);
          },
        },
        playerVars: {
          cc_lang_pref: captionLanguage || undefined,
          // Requests captions; a viewer's own YouTube preference can still override it,
          // and it's least reliable on auto-generated tracks. hl is set alongside because
          // the two are reported to work better in agreement.
          cc_load_policy: 1,
          hl: captionLanguage || undefined,
          playsinline: 1,
          rel: 0,
        },
        videoId: videoIdRef.current,
      });
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("[MediaClips] player failed to initialise, falling back to a plain embed", error);

      // Reset so a later mount can try again rather than reusing a permanently failed one.
      apiPromise = null;

      if (!cancelled) setFailed(true);
    });

    return () => {
      cancelled = true;

      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [captionLanguage, consented]);

  // Swap clips in place. Skipped for the clip the player was constructed with, so opening
  // the page doesn't autoplay — loadVideoById starts playback, which is what you want from
  // a click and not what you want on arrival.
  useEffect(() => {
    if (!ready || !playerRef.current || !videoId) return;
    if (loadedRef.current === videoId) return;

    loadedRef.current = videoId;
    playerRef.current.loadVideoById(videoId);
  }, [ready, videoId]);

  const pause = useCallback(() => {
    // getPlayerState is 1 while playing. Checking first means resume() can't start a video
    // the learner had deliberately paused.
    if (!playerRef.current || !playerRef.current.getPlayerState) return false;
    if (playerRef.current.getPlayerState() !== 1) return false;

    playerRef.current.pauseVideo();
    return true;
  }, []);

  const play = useCallback(() => {
    if (playerRef.current && playerRef.current.playVideo) playerRef.current.playVideo();
  }, []);

  // Relative seek. The second argument tells the player to seek immediately rather than
  // waiting for the next keyframe, which matters at this scale - a 10 second rewind that
  // lands 3 seconds off is worse than useless when you're re-listening to one phrase.
  const seek = useCallback((offsetSeconds) => {
    if (!playerRef.current || !playerRef.current.getCurrentTime) return;

    const target = playerRef.current.getCurrentTime() + offsetSeconds;

    playerRef.current.seekTo(Math.max(0, target), true);
  }, []);

  const toggle = useCallback(() => {
    if (!playerRef.current || !playerRef.current.getPlayerState) return;

    if (playerRef.current.getPlayerState() === 1) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  }, []);

  return { acceptCookies, consented, containerRef, failed, pause, play, playing, ready, seek, toggle };
}
